import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { ChatMessage, WorkflowStatus } from "../types/redux";
import {
  type ClearTurnTodoPlanPayload,
  type SetTurnTodoPlanPayload,
  type StageTurnResultsPayload,
  type UpdateMessagePayload,
  initialState,
  WELCOME_MESSAGE_TEXT,
} from "./chatSliceTypes";

const chatSlice = createSlice({
  name: "chatSlice",
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },
    updateMessageByClientId: (state, action: PayloadAction<UpdateMessagePayload>) => {
      const { clientMessageId, text, updates } = action.payload || {};
      if (!clientMessageId) return;
      const target = state.messages.find(
        (message) => message.clientMessageId === clientMessageId
      );
      if (!target) return;

      if (updates && typeof updates === "object") {
        Object.assign(target, updates);
      }

      if (text !== undefined) {
        target.text = text;
      }
    },
    removeMessageByClientId: (state, action: PayloadAction<string>) => {
      const clientMessageId = action.payload;
      if (!clientMessageId) return;
      state.messages = state.messages.filter(
        (message) => message.clientMessageId !== clientMessageId
      );
    },
    updateMessageText: (state, action: PayloadAction<{ index: number; text?: string }>) => {
      const { index, text } = action.payload;
      if (index >= 0 && index < state.messages.length && text !== undefined) {
        state.messages[index].text = text;
      }
    },
    setBotTyping: (state, action: PayloadAction<boolean>) => {
      state.isBotTyping = action.payload;
    },
    setMcpEnvironment: (state, action: PayloadAction<string>) => {
      state.mcpEnvironment = action.payload;
    },
    setDataVersion: (state, action: PayloadAction<number>) => {
      state.dataVersion = action.payload;
    },
    setConversationId: (state, action: PayloadAction<string | null>) => {
      state.conversationId = action.payload;
    },
    setBootstrapInitialized: (state, action: PayloadAction<boolean>) => {
      state.bootstrapInitialized = action.payload;
    },
    setBootstrapError: (state, action: PayloadAction<string | null>) => {
      state.bootstrapError = action.payload;
    },
    removeMessageByType: (state, action: PayloadAction<string>) => {
      state.messages = state.messages.filter((msg: ChatMessage) => msg.type !== action.payload);
    },
    resetChatForNewProject: (state) => {
      // Reset chat state for a new project while preserving welcome message
      state.messages = [{ sender: "chatbot", text: WELCOME_MESSAGE_TEXT }];
      state.isBotTyping = false;
      state.bootstrapInitialized = false;
      state.bootstrapError = null;
      state.conversationId = null;
      state.pendingStreamingMessages = {};
      state.currentWorkflowStatus = null;
      state.chatInputReady = true; // Unlock input for fresh projects
      state.useCaseSelected = false; // Reset use case selection for new project
      state.selectedUseCase = null; // Reset specific use case for new project
      state.pendingTurnResults = {}; // Phase 3: Clear staged results
      state.todoPlanByTurn = {}; // Clear todo plans
      // Note: mcpEnvironment and dataVersion are preserved
    },
    setWorkflowStatus: (state, action: PayloadAction<WorkflowStatus | null>) => {
      // Update workflow status for action "thinking" indicators
      state.currentWorkflowStatus = action.payload;
    },
    setChatInputReady: (state, action: PayloadAction<boolean>) => {
      state.chatInputReady = action.payload;
    },
    setUseCaseSelected: (state, action: PayloadAction<boolean>) => {
      state.useCaseSelected = action.payload;
    },
    setSelectedUseCase: (state, action: PayloadAction<string | null>) => {
      state.selectedUseCase = action.payload;
      // Also mark use case as selected when a specific use case is set
      if (action.payload) {
        state.useCaseSelected = true;
      }
    },
    stageTurnResults: (state, action: PayloadAction<StageTurnResultsPayload>) => {
      // Phase 3: Stage results payload for a turn to be merged when chat_response finalizes
      const { turnId, results, documents } = action.payload;
      if (turnId && state.pendingTurnResults) {
        state.pendingTurnResults[turnId] = { results, documents };
      }
    },
    setTurnTodoPlan: (state, action: PayloadAction<SetTurnTodoPlanPayload>) => {
      // Store todo plan for a turn, keyed by turnId
      const { turnId, plan, scopeSummary } = action.payload;
      console.log("📝 setTurnTodoPlan reducer called:", { turnId, plan, scopeSummary });
      if (turnId && plan && state.todoPlanByTurn) {
        state.todoPlanByTurn[turnId] = { plan, scopeSummary };
        console.log("✅ todoPlanByTurn updated:", state.todoPlanByTurn);
      } else {
        console.log("⚠️ setTurnTodoPlan: missing turnId or plan");
      }
    },
    clearTurnTodoPlan: (state, action: PayloadAction<ClearTurnTodoPlanPayload>) => {
      // Clear todo plan for a specific turn
      const { turnId } = action.payload;
      if (turnId && state.todoPlanByTurn && state.todoPlanByTurn[turnId]) {
        delete state.todoPlanByTurn[turnId];
      }
    },
    setChatHistoryLoading: (state, action: PayloadAction<boolean>) => {
      state.chatHistoryLoading = action.payload;
    },
    setChatHistoryError: (state, action: PayloadAction<string | null>) => {
      state.chatHistoryError = action.payload;
    },
    setMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      // Replace all messages with the provided array
      state.messages = action.payload;
    },
  },
});

export const {
  addMessage,
  setBotTyping,
  setMcpEnvironment,
  setDataVersion,
  setConversationId,
  setBootstrapInitialized,
  setBootstrapError,
  resetChatForNewProject,
  setWorkflowStatus,
  setChatInputReady,
  setUseCaseSelected,
  setSelectedUseCase,
  stageTurnResults,
  setTurnTodoPlan,
  clearTurnTodoPlan,
  setChatHistoryLoading,
  setChatHistoryError,
  setMessages,
  removeMessageByType,
  updateMessageByClientId,
  removeMessageByClientId,
  updateMessageText,
} = chatSlice.actions;

// Helper action creators for chat input lock
export const lockChatInput = () => setChatInputReady(false);
export const unlockChatInput = () => setChatInputReady(true);

export default chatSlice.reducer;
